/* eslint-disable @typescript-eslint/no-explicit-any */
import { isRouteErrorResponse, useLocation, useNavigate, useRouteError } from 'react-router-dom';
import Button from '../../components/Button/Button';
import { ButtonVariants } from '../../components/Button/Button.types';
import Navigation from '../../components/Navigation/Navigation';
import icon from '../../images/404.svg';
import logo from '../../images/logo.svg';
import { useCurrentUser } from '../../services/user/user.service';
import { UserTypes } from '../../types/auth.types';
import styles from './ErrorPage.module.css';
import { useTranslation } from 'react-i18next';

const ErrorPage = () => {
  const { t } = useTranslation();
  const error: unknown = useRouteError();
  const navigate = useNavigate();
  const location = useLocation();
  const { data, isLoading } = useCurrentUser(true);
  const handleBack = () => navigate(location.pathname);
  const handleBack404 = () => navigate(-1);
  const handleHome = () => navigate('/');

  const is404 = isRouteErrorResponse(error) && error.status === 404;
  const isNon404RouterError = isRouteErrorResponse(error) && error.status !== 404;
  const isNonRouterError = error instanceof Error;

  const errorMessage = () => {
    // if the error is from react router (determined by their provided type check) we can show the 404 page or the message from react router
    if (is404) return t('errorPage.pageNotFound');
    if (isNon404RouterError) return error.data.message;
    // if the error is a javascript Error, we know it has a message
    if (isNonRouterError) return error.message;
    // if we can't determine the type of the error, show a generic error message
    return t('errorPage.somethingWentWrong');
  };

  if (isLoading) return null;

  return (
    <div className={styles.pageLayout}>
      {data?.data && <Navigation isAdmin={data.data.type === UserTypes.ADMIN} />}
      <div id="error-page" data-testid="errorPage" className={styles.errorPage}>
        <div className={styles.banner} />
        <div className={styles.logo}>
          <img src={logo} alt="logo" />
          {is404 && <img src={icon} alt="404" />}
        </div>
        <h1>{errorMessage()}</h1>
        <div className={styles.buttons}>
          <Button variant={ButtonVariants.Secondary} onClick={is404 ? handleBack404 : handleBack}>
            {t('common.back')}
          </Button>
          <Button onClick={handleHome}>{t('common.home')}</Button>
        </div>
      </div>
    </div>
  );
};

export default ErrorPage;
